/**
 * Shell - API call logging
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/utils/apiLogger.js
 *
 * Logs one line when an API request goes out and one when it comes back, with a
 * SHAPE summary of the response instead of its body.
 *
 *     → GET  /v1/ops-data/asset-availability  cid:a3f1  {window:8h}
 *     ← 200  /v1/ops-data/asset-availability  cid:a3f1    84ms  {segments:412, machines:12}  18KB
 *
 * Installed once, in the shell, by wrapping `window.fetch`. That is deliberate:
 * `createAPIClient` already logs a status line, but ~40 files across the ten
 * repos call `fetch()` directly and bypass it — AuthContext, regionService, every
 * useCompanyHierarchy hook — and each repo carries its own copy of apiClient.js.
 * One wrapper at the boundary covers every caller, including the remotes, with no
 * call-site changes.
 *
 * THIS MODULE OBSERVES ONLY. It must never alter what a caller sees:
 *   - the original Response object is returned untouched; the body is read from a
 *     `clone()`, in the background, after the caller already has its response
 *   - a rejected fetch is re-thrown as-is, same error, same stack
 *   - every logging step is wrapped, so a fault here can never fail a request
 *   - non-API traffic (remoteEntry.js, manifests, HMR, fonts) passes through
 *     unwrapped and unlogged
 */

import { createLogger } from './logger';

const logger = createLogger('Shell::API');

// Query params that identify the plumbing rather than the call.
const NOISE_PARAMS = new Set(['client_id', 'correlation_id', 'source']);

// Never render a value under these keys, at any depth we render.
const SECRET_KEYS = /^(password|token|secret|authorization|access_token|refresh_token|api_key)$/i;

// Only clone-and-read JSON bodies below this. Larger ones report size only.
const MAX_SUMMARY_BYTES = 512 * 1024;

/** Is this one of ours, or is it a remoteEntry / font / HMR ping? */
const isApiCall = (url) => url.pathname.includes('/api/') || /^\/v\d+\//.test(url.pathname);

const parseUrl = (value) => {
    try {
        return new URL(value, window.location.origin);
    } catch {
        return null;
    }
};

/** `?email=ann@example.com` → `l***@example.test`. Pre-auth, shared terminals. */
const maskEmail = (value) => {
    const at = value.indexOf('@');
    return at > 0 ? `${value[0]}***${value.slice(at)}` : '***';
};

/** The params worth seeing, minus the plumbing, minus anything sensitive. */
const summariseParams = (url) => {
    const parts = [];
    for (const [key, value] of url.searchParams) {
        if (NOISE_PARAMS.has(key)) continue;
        if (SECRET_KEYS.test(key)) { parts.push(`${key}:«redacted»`); continue; }
        parts.push(`${key}:${key === 'email' ? maskEmail(value) : String(value).slice(0, 32)}`);
    }
    return parts.length ? ` {${parts.slice(0, 6).join(', ')}}` : '';
};

/**
 * Shape, never content: top-level keys, and the LENGTH of any array.
 * This is the whole point — `{segments:412}` tells you the call worked and how
 * much came back, without 412 objects in the console.
 */
const summariseBody = (value) => {
    if (Array.isArray(value)) return `[${value.length}]`;
    if (value === null || typeof value !== 'object') return String(value).slice(0, 40);

    const entries = Object.entries(value);
    const parts = entries.slice(0, 8).map(([key, v]) => {
        if (SECRET_KEYS.test(key)) return `${key}:«redacted»`;
        if (Array.isArray(v)) return `${key}:${v.length}`;
        if (v !== null && typeof v === 'object') return `${key}:{…}`;
        return `${key}:${String(v).slice(0, 24)}`;
    });
    if (entries.length > parts.length) parts.push(`+${entries.length - parts.length} more`);
    return `{${parts.join(', ')}}`;
};

const formatBytes = (n) =>
    n < 1024 ? `${n}B` : n < 1024 * 1024 ? `${Math.round(n / 1024)}KB` : `${(n / 1048576).toFixed(1)}MB`;

/**
 * Read the response shape from a clone, after the caller already has the original.
 * Never awaited by the request path — a slow or unreadable body delays nothing.
 */
const describeBody = async (response, label) => {
    try {
        const type = response.headers.get('content-type') || '';
        const declared = Number(response.headers.get('content-length')) || 0;

        if (!type.includes('json')) {
            return declared ? `${type.split(';')[0] || 'body'} ${formatBytes(declared)}` : '';
        }
        if (declared > MAX_SUMMARY_BYTES) return `json ${formatBytes(declared)} (too large to summarise)`;

        const text = await response.clone().text();
        if (!text) return '';

        const size = formatBytes(new Blob([text]).size);
        try {
            return `${summariseBody(JSON.parse(text))}  ${size}`;
        } catch {
            return `${label === 'error' ? text.slice(0, 200) : 'non-JSON'}  ${size}`;
        }
    } catch {
        return '';   // a consumed or streaming body is not a reason to say anything
    }
};

export const installApiLogging = () => {
    if (typeof window === 'undefined' || window.__QUANTIIX_API_LOGGING__) return;
    window.__QUANTIIX_API_LOGGING__ = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
        let meta = null;

        try {
            const raw = typeof input === 'string' ? input
                : input instanceof URL ? input.href
                : input?.url;
            const url = raw ? parseUrl(raw) : null;

            if (url && isApiCall(url)) {
                // A Request object's method lives on the Request, not on init.
                const method = (init?.method || input?.method || 'GET').toUpperCase();
                const cid = url.searchParams.get('correlation_id');
                const shortCid = cid ? `  cid:${cid.slice(0, 4)}` : '';

                meta = { method, path: url.pathname, shortCid, cid, start: performance.now() };
                logger.info(`→ ${method.padEnd(4)} ${url.pathname}${shortCid}${summariseParams(url)}`);
            }
        } catch {
            meta = null;   // never let bookkeeping stop a request
        }

        try {
            const response = await nativeFetch(input, init);

            if (meta) {
                const ms = Math.round(performance.now() - meta.start);
                // Fire-and-forget: the caller already has `response`.
                describeBody(response, response.ok ? 'ok' : 'error').then((shape) => {
                    const line =
                        `← ${String(response.status).padEnd(4)} ${meta.path}${meta.shortCid}  ` +
                        `${String(ms).padStart(5)}ms  ${shape}`.trimEnd();
                    if (response.ok) logger.info(line);
                    else logger.error(line);
                }).catch(() => {});
            }

            return response;
        } catch (error) {
            if (meta) {
                const ms = Math.round(performance.now() - meta.start);
                logger.error(
                    `← FAIL ${meta.path}${meta.shortCid}  ${String(ms).padStart(5)}ms  ${error?.message ?? error}`
                );
            }
            throw error;   // same error, same stack — the caller sees no difference
        }
    };

    logger.debug('API call logging installed');
};
